// @flow

import _ from 'lodash';
import memoize from 'memoize-weak';
import { $set, update } from 'qim';

import { Colors } from '@zapier/theme';
import { isLiveDataRequired } from '@zapier/editor-core';
import {
  serviceUtils as commonServiceUtils,
  decodeHTMLEntities,
} from '@zapier/common-utils';

import _str from 'app/common/underscore-string';
import fuzzyscore from 'app/common/fuzzyscore';
import fuzzysearch from 'app/common/fuzzysearch';
import isServiceHidden from 'app/service/utils/isServiceHidden';
import { NAME, nodeTypes } from 'app/service/constants';
import {
  compareVersions,
  exists,
  get,
  superHumanize,
} from 'app/common/CommonUtils';

import type { FlowNode, FlowNodeType } from '@zapier/editor-core';
import type {
  Service,
  ServiceAction,
  ServiceField,
  ServicesMap,
} from 'app/service/types';

const { getVersion, stripVersion } = commonServiceUtils;

const PATH_API = 'BranchingAPI';
const isPathApi = (api?: string) => api === PATH_API;
const isPathTrue = (path, service) => Boolean(_.get(service, path, null));
const isAnyPathTrue = (paths, service) =>
  paths.some(path => isPathTrue(path, service));

// The multiple keys here represent legacy data structure
export const isBeta = _.partial(isAnyPathTrue, ['beta', 'isBeta']);
export const isBuiltIn = (service: Service) =>
  service.isBuiltIn || _.get(service, 'builtIn');
export const isDeprecated = _.partial(isAnyPathTrue, [
  'deprecated',
  'isDeprecated',
]);
export const isFeatured = (service: Service) =>
  service.isFeatured || _.get(service, 'featured');
export const isHidden = isServiceHidden;
export const isPremium = _.partial(isAnyPathTrue, ['premium', 'isPremium']);
export const isInvite = (service: Service) =>
  service.isInvite || _.get(service, 'invite_app');
export const isPublic = (service: Service) =>
  service.isPublic || _.get(service, 'public');
export const isOauth = (service: Service) => {
  const authType = _.get(service, 'authType', null);
  const oauthService = _.get(service, ['meta', 'oauth'], false);
  return authType === 'oauth' || oauthService;
};
export const hasUpfrontFields = (service: Service) => {
  return (
    Boolean(service.hasUpfrontFields) || !_.isEmpty(service.upfront_fields)
  );
};
export const excludePathsApi = (services: Service[]) =>
  _.filter(services, ({ key }) => !isPathApi(key));

const actionHelpTextHTML = (action: ServiceAction) => {
  // This flag is put here by the reducer. So depending on it is a bit of a
  // smell. But it's probably non-trivial to refactor that out.
  if (action.hasCreate) {
    return `<p>${action.help_text} Optionally, create one if none are found.</p>`;
  }

  return action.help_text_html;
};

const actionHelpText = (action: ServiceAction) => {
  return decodeHTMLEntities(actionHelpTextHTML(action).replace(/<[^>]*>/g, ''));
};

type SelectListItemsOptions = { isRestrictedChoice: boolean };
const makeSelectListItems = (
  node,
  actions: ?(ServiceAction[]),
  itemType,
  { isRestrictedChoice }: SelectListItemsOptions = { isRestrictedChoice: false }
) => {
  const allUnimportant = _.every(
    actions,
    action => (action.important || false) === false
  );

  return _.chain(actions)
    .filter(action => {
      // Ensures that actions that are { `hide`: true } are displayed if the node is set to that action
      if (node.action === action.key) {
        if (
          node.type_of === itemType ||
          (node.type_of === 'search_or_write' && itemType === 'search')
        ) {
          return true;
        }
      }
      if (isRestrictedChoice) {
        return false;
      }
      return !action.hide;
    })
    .map(action => {
      const helpText = actionHelpText(action);
      const helpTextHTML = actionHelpTextHTML(action);
      return {
        important: allUnimportant ? true : action.important || false,
        label: action.label,
        helpText,
        helpTextHTML,
        itemKey: action.key,
        itemType,
      };
    })
    .value();
};

export const transformSelectListItems = (
  node: FlowNode,
  service: Service,
  opts: SelectListItemsOptions
) => {
  const items = [];

  if (node.type_of === 'read') {
    items.push({
      itemType: 'read',
      header: '',
      items: makeSelectListItems(node, service.read, 'read', opts),
    });
  } else {
    const writeItems = makeSelectListItems(node, service.write, 'write', opts);

    if (writeItems.length) {
      items.push({
        itemType: 'write',
        header: 'Create',
        items: writeItems,
      });
    }

    const searchItems = makeSelectListItems(
      node,
      service.search,
      'search',
      opts
    );

    if (searchItems.length) {
      items.push({
        itemType: 'search',
        header: 'Search',
        items: searchItems,
      });
    }

    const filterItems = makeSelectListItems(
      node,
      service.filter,
      'filter',
      opts
    );

    if (filterItems.length) {
      items.push({
        itemType: 'filter',
        header: 'Filter',
        items: filterItems,
      });
    }
  }

  return items;
};

export type ServiceRecommendation = {
  slug: string,
  score: number,
};

const normalizeActions = (actions: ServiceAction[]): ServiceAction[] => {
  actions = actions.slice(0);
  if (!_.some(actions, action => get(action, ['important']))) {
    return actions.map(action => {
      const label = exists(action.label)
        ? action.label
        : superHumanize(action.key);
      return update(
        [
          ['label', $set(label)],
          ['important', $set(true)],
        ],
        action
      );
    });
  }
  return actions;
};

const visibleActions = (actions: ServiceAction[], currentSelection: mixed) => {
  return _.filter(actions, action => {
    return get(action, ['key']) === currentSelection || !get(action, ['hide']);
  });
};

const parseActions = (actions: ServiceAction[], currentSelection: mixed) => {
  /* special parsing rules to help the frontend.
   * 1. if all fields are `important`: false or not defined, set them all to true
   * 2. give every action a label if it doesnt have one
   * 3. strip hidden actions (unless it is currently selected one)
   */
  actions = normalizeActions(actions);
  return visibleActions(actions, currentSelection);
};

const getActionMeta = (actions: ServiceAction[], actionKey: string) => {
  actions = parseActions(actions, actionKey);
  return _.find(actions, action => action.key === actionKey);
};

export const actionMeta = (
  service: Service,
  actionKey: string,
  typeOf: FlowNodeType = nodeTypes.READ
) => {
  if (!service || !service[typeOf]) {
    return null;
  }
  return getActionMeta(service[typeOf], actionKey);
};

const getActionName = (
  action: ?ServiceAction,
  { hasNoVerb = false }: { hasNoVerb: boolean }
) => {
  if (action == null) {
    return '';
  }

  if (hasNoVerb && action.noun) {
    // hasNoVerb == wants a noun back
    return action.noun;
  }

  let name;
  if (action.label != null) {
    name = action.label;
  } else {
    name = superHumanize(action.key);
  }

  if (hasNoVerb) {
    // clean name and split into words
    let words = _str.words(
      name
        .replace(/\([^)]\)/g, '') // (some text)
        .replace(/\[[^\]]\]/g, '') // [some text]
        .replace(/\s{2,}/g, ' ') // dupe white spaces
    );

    if (words.length > 1) {
      const flags = ['new', 'old', 'deprecated', 'legacy'];
      const verbs = [
        'new',
        'updated',
        'update',
        'changed',
        'delete',
        'create',
        'added',
        'add',
        'set',
        'send',
        'submit',
        'call',
        'order',
        'catch',
        'copy',
        'append',
        'moved',
        'move',
        'remove',
        'change',
        'turn',
        'start',
        'stop',
        'post',
        'record',
        'grant',
        'subscribe',
        'make',
      ];
      const articles = ['a', 'an', 'the'];
      const prepositions = ['on', 'for', 'to', 'in', 'from', 'within', 'by'];
      const conjuctions = ['and', 'or', 'but', 'nor'];
      let verbFound = -1;
      let nounFound = -1;
      let approvedWords = [];
      words.every((word, index, array) => {
        const lowerWord = word.toLowerCase();
        // No word
        if (/^[-]$/.test(word)) {
          return true;
        }
        // Articles can always be missed
        if (articles.indexOf(lowerWord) !== -1) {
          return true;
        }
        // Flags
        // 'Deprecated - Create Contact' > 'Contact',
        // 'Create Contact - Deprecated' > 'Contact',
        if (
          (index === 0 || index === array.length) &&
          flags.indexOf(lowerWord) !== -1
        ) {
          return true;
        }
        // Remove list when it has a verb
        // 'Create/Whatever Tracker' > 'Tracker'
        // 'Create Post/Copy' > 'Post/Copy'
        if (
          nounFound === -1 &&
          verbFound === -1 &&
          lowerWord.split(/\\|\//).some(val => verbs.indexOf(val) !== -1)
        ) {
          verbFound = index;
          return true;
        }
        // Remove list when it has a conjunction before finding a noun
        // 'New or Updated Spreadsheet Row' > 'Updated Spreadsheet Row'
        if (nounFound === -1 && conjuctions.indexOf(lowerWord) !== -1) {
          return true;
        }
        if (prepositions.indexOf(lowerWord) !== -1) {
          // Nothing following preposition will add meaning to noun
          // 'Create a Tracker on Google' > 'Tracker'
          if (nounFound !== -1) {
            return false;
          } else {
            // Cannot expect noun after preposition
            // 'Post to Facebook' > 'Post to Facebook'
            // 'Subscribe to Campaign' > 'Subscribe to Campaign'
            approvedWords = words;
            return false;
          }
        }
        if (nounFound === -1) {
          nounFound = index;
        }
        approvedWords.push(word);
        return true;
      });
      if (approvedWords.length > 0) {
        words = approvedWords;
      }
    }
    name = words.join(' ');
  }

  name = _str.trim(name);
  return name;
};

export const actionName = (
  service: Service,
  actionKey: string,
  typeOf: FlowNodeType = nodeTypes.READ,
  options: { hasNoVerb: boolean }
) => {
  const action = actionMeta(service, actionKey, typeOf);
  return getActionName(action, options);
};

export const actionNoun = (
  service: Service,
  actionKey: string,
  typeOf: FlowNodeType = nodeTypes.READ
) => {
  if (typeOf === nodeTypes.SEARCH_OR_WRITE) {
    // Meta for a search_or_write doesn't contain the nouns of either, so we default to use search
    typeOf = nodeTypes.SEARCH;
  }

  const noun = actionName(service, actionKey, typeOf, {
    hasNoVerb: true,
  });
  return (noun || 'Item').toString();
};

export const rawUpfronts = (
  upfrontFields: Array<ServiceField>
): Array<ServiceField> => {
  if (!upfrontFields) {
    return [];
  }

  return upfrontFields.filter(field => {
    return !(exists(field.important) && field.important === false);
  });
};

const ServiceUtils = {
  name(service: Service = {}) {
    if (service.name) {
      return service.name;
    }

    if (service.title) {
      return service.title;
    }

    if (exists(service.meta, ['human_readable_key'])) {
      return get(service.meta, ['human_readable_key']);
    }

    return service.key || 'Unnamed service';
  },

  nameFromApi(api: string, services: ServicesMap | {}) {
    return ServiceUtils.name(services[api]);
  },

  requiresLiveData(service: Service) {
    // For now, we use app source in ZT only for Formatter and Code
    return isLiveDataRequired(service.key);
  },

  rawUpfronts,

  actionName,

  actionNoun,

  allVisibleLabelsOfType(
    type: 'reads' | 'searches' | 'writes',
    service: Service
  ) {
    return _.chain(service)
      .get(['actions', type])
      .filter(action => !_.get(action, 'hide'))
      .map(action => _.get(action, 'label'))
      .filter(Boolean)
      .uniq()
      .sortBy(noun => (noun || '').toLowerCase())
      .value();
  },

  allVisibleReadLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('reads', service);
  },

  allVisibleWriteLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('writes', service);
  },

  allVisibleSearchLabels(service: Service) {
    return ServiceUtils.allVisibleLabelsOfType('searches', service);
  },

  actionMeta,

  /*
   * Finds the action associated with the passed in service and current action.
   * Useful for e.g. finding the 'search' action corresponding to a 'search_or_write' action, or vice-versa.
   */
  matchesQuery: (query: string, service: Object) => {
    return fuzzysearch(query, service[NAME]);
  },

  queryScore: (query: string, service: Object) => {
    return -fuzzyscore(query, service[NAME]);
  },

  search: memoize((query: string, services: Array<Service>) => {
    if (!query) {
      return services;
    }

    return services
      .filter(_.partial(ServiceUtils.matchesQuery, query))
      .sort((serviceA, serviceB) => {
        const compareQueryScore =
          ServiceUtils.queryScore(query, serviceA) -
          ServiceUtils.queryScore(query, serviceB);
        if (compareQueryScore) {
          return compareQueryScore;
        }

        return 0;
      });
  }),

  getCommonProblemsHelpLink: (service: any = {}) => {
    return service.slug
      ? `/apps/${service.slug}/help#common-problems-${service.slug}`
      : `/help/search?q=${encodeURIComponent(service.name)}`;
  },

  // This function will filter out any Service that does not contain an `id`.
  // According to the flow type for Service, it is possible to have an `id` that
  // is `null` or `undefined`.
  // https://github.com/zapier/zapier/blob/5540c1adc7c0de3f23456d0a3b656311b6d490cd/web/frontend/assets/app/service/types.js#L35
  getServicesById(services: Array<Service>): ServicesMap {
    return _.reduce(
      services,
      (acc, service) => {
        if (service && service.id) {
          acc[service.id] = service;
        }

        return acc;
      },
      {}
    );
  },

  getServiceByKey(services: ServicesMap | Service[], serviceKey: ?string) {
    return _.find(services, ({ key }) => key === serviceKey);
  },

  getServiceByKeyWithOptionalVersion(
    services: ServicesMap | Service[],
    serviceKey: string
  ) {
    const withAnyVersion = `${stripVersion(serviceKey)}@`;
    const possibleServices = _.filter(
      services,
      ({ key }) =>
        key === serviceKey || (key || '').indexOf(withAnyVersion) === 0
    );
    const selectedService = possibleServices
      .sort((a, b) => {
        if (a.key === serviceKey) {
          return 1;
        }
        if (b.key === serviceKey) {
          return -1;
        }
        return compareVersions(getVersion(a.key), getVersion(b.key));
      })
      .reverse()[0];
    return selectedService;
  },

  getServiceName(service: Service) {
    return _.trim((service[NAME] || '').replace('by Zapier', ''));
  },

  getPrimaryColor(service: Service) {
    if (isBuiltIn(service)) {
      return Colors.lava;
    }

    const primaryColor = _.get(service, ['primaryColor']);
    return primaryColor ? `#${primaryColor}` : Colors.heroine;
  },

  isBuiltIn,
  isDeprecated,
  isHidden,
  isFeatured,
  isPremium,
  isPublic,
  isBeta,
  isOauth,
  isInvite,
  hasUpfrontFields,
  excludePathsApi,
};

export default ServiceUtils;
